import React, { useState, useRef, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface SmartNotesTabsProps {
  content: string
  setContent: (c: string) => void
  syncNodeData: (d: any) => void
  urls: string[]
  isLocked: boolean
  handleAnalyze: () => void
  handleCreateDocument: (recommendation: string) => void
  data: any
  linkedKnowledge?: string
  onLinkedKnowledgeChange?: (lk: string) => void
  hideLinksAndAnalyze?: boolean
}

const handleScroll = (element: HTMLElement | null, deltaY: number) => {
  if (!element) return false
  const { scrollTop, scrollHeight, clientHeight } = element
  const isAtTop = scrollTop <= 0
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2
  const isScrollingUp = deltaY < 0
  const isScrollingDown = deltaY > 0
  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY
    return true
  }
  return false
}

const SmartNotesTabs: React.FC<SmartNotesTabsProps> = ({
  content,
  setContent,
  syncNodeData,
  isLocked,
  data,
  linkedKnowledge,
  onLinkedKnowledgeChange,
  hideLinksAndAnalyze,
}) => {
  const [activeTab, setActiveTab] = useState<
    'information' | 'context' | 'linkedKnowledge'
  >('information')
  const [contextValue, setContextValue] = useState(data.context || '')
  const [infoValue, setInfoValue] = useState(content)

  // Keep contextValue in sync with data.context
  useEffect(() => {
    setContextValue(data.context || '')
  }, [data.context])

  // Keep infoValue in sync with content prop
  useEffect(() => {
    setInfoValue(content)
  }, [content])

  // Refs for each textarea
  const infoTextareaRef = useRef<HTMLTextAreaElement>(null)
  const contextTextareaRef = useRef<HTMLTextAreaElement>(null)
  const linkedKnowledgeTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Attach wheel listeners directly to textareas
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation()
    }

    // Use a timeout to ensure DOM elements are rendered after tab change
    const timeoutId = setTimeout(() => {
      let currentTextarea: HTMLTextAreaElement | null = null

      if (activeTab === 'information') {
        currentTextarea = infoTextareaRef.current
      } else if (activeTab === 'context') {
        currentTextarea = contextTextareaRef.current
      } else if (activeTab === 'linkedKnowledge') {
        currentTextarea = linkedKnowledgeTextareaRef.current
      }

      if (currentTextarea) {
        currentTextarea.addEventListener('wheel', handleWheel)
      }
    }, 0)

    // Cleanup function
    return () => {
      clearTimeout(timeoutId)
      // Remove listeners from all textareas
      if (infoTextareaRef.current) {
        infoTextareaRef.current.removeEventListener('wheel', handleWheel)
      }
      if (contextTextareaRef.current) {
        contextTextareaRef.current.removeEventListener('wheel', handleWheel)
      }
      if (linkedKnowledgeTextareaRef.current) {
        linkedKnowledgeTextareaRef.current.removeEventListener(
          'wheel',
          handleWheel
        )
      }
    }
  }, [activeTab])

  return (
    <div className="w-full h-full">
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab as any}
        className="w-full h-full flex flex-col"
      >
        <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
          <TabsTrigger value="information">Information</TabsTrigger>
          <TabsTrigger value="context">Context</TabsTrigger>
          <TabsTrigger value="linkedKnowledge">Linked Knowledge</TabsTrigger>
        </TabsList>
        <TabsContent
          value="information"
          className="mt-2 flex-grow"
          style={{ height: 'calc(100% - 50px)' }}
        >
          <textarea
            ref={infoTextareaRef}
            className="w-full h-full p-2 border rounded nodrag resize-none"
            placeholder="Paste or type your information here for analysis. You can also paste HTTP links to analyze their content."
            value={infoValue}
            onChange={(e) => {
              setInfoValue(e.target.value)
            }}
            onBlur={(e) => {
              syncNodeData({ content: e.target.value })
            }}
            disabled={isLocked}
          />
        </TabsContent>
        <TabsContent
          value="context"
          className="mt-2 flex-grow"
          style={{ height: 'calc(100% - 50px)' }}
        >
          <textarea
            ref={contextTextareaRef}
            className="w-full h-full p-2 border rounded nodrag resize-none"
            value={contextValue}
            onChange={(e) => {
              setContextValue(e.target.value)
            }}
            onBlur={(e) => {
              syncNodeData({ context: e.target.value })
            }}
            placeholder="Add context for this node (optional)..."
            disabled={isLocked}
          />
        </TabsContent>
        <TabsContent
          value="linkedKnowledge"
          className="mt-2 flex-grow"
          style={{ height: 'calc(100% - 50px)' }}
        >
          <textarea
            ref={linkedKnowledgeTextareaRef}
            className="w-full h-full p-2 border rounded nodrag resize-none"
            value={linkedKnowledge || ''}
            onChange={(e) => onLinkedKnowledgeChange?.(e.target.value)}
            onBlur={(e) => {
              syncNodeData({ linkedKnowledge: e.target.value })
            }}
            placeholder="Linked knowledge will appear here."
            disabled={isLocked}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default SmartNotesTabs
